import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { 
  Calendar, 
  Globe, 
  Briefcase, 
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  Heart,
} from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to ExpatsSphere!',
    description: 'Your global community for connecting with expats worldwide. Let us show you around!',
    icon: Sparkles,
    color: 'from-primary to-accent',
  },
  {
    id: 'events',
    title: 'Discover & Create Events',
    description: 'Find local meetups, cultural events, and social gatherings — or host your own. Never miss a chance to connect!',
    icon: Calendar,
    color: 'from-purple-500 to-purple-600',
    link: '/events',
  },
  {
    id: 'connect',
    title: 'Connect with Expats',
    description: 'Find fellow expats by nationality, city, or interests. Join groups, send messages, and build your global network.',
    icon: Globe,
    color: 'from-green-500 to-green-600',
    link: '/find-expats',
  },
  {
    id: 'services',
    title: 'Jobs, Housing & Marketplace',
    description: 'Browse jobs with visa sponsorship, find expat-friendly housing, and buy/sell items with fellow expats.',
    icon: Briefcase,
    color: 'from-amber-500 to-amber-600',
    link: '/jobs',
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    description: 'Start exploring and connecting with expats around the world. Welcome to the community!',
    icon: Heart,
    color: 'from-red-500 to-pink-500',
  },
];

const TutorialTour = ({ open, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const step = TOUR_STEPS[currentStep];
  const IconComponent = step.icon;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    onClose();
  };

  const handleGoToFeature = () => {
    if (step.link) {
      onClose();
      navigate(step.link);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-0 rounded-3xl [&>button[class*='opacity-70']]:hidden">
        {/* Header with gradient */}
        <div className={`bg-gradient-to-br ${step.color} p-8 text-white relative`}>
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close tutorial"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
              <IconComponent className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{step.title}</h2>
            <p className="text-white/90 text-sm leading-relaxed">
              {step.description}
            </p>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 py-4 bg-white">
          {TOUR_STEPS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentStep 
                  ? 'bg-primary w-6' 
                  : index < currentStep 
                    ? 'bg-primary/50' 
                    : 'bg-gray-200'
              }`}
              aria-label={`Go to step ${index + 1}`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="p-6 pt-2 bg-white space-y-3">
          {step.link && !isLastStep && (
            <Button
              onClick={handleGoToFeature}
              variant="outline"
              className="w-full h-12 rounded-full font-semibold"
            >
              Try it now
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          <div className="flex gap-3">
            {!isFirstStep && (
              <Button
                onClick={handlePrev}
                variant="ghost"
                className="flex-1 h-12 rounded-full font-semibold"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              className={`${isFirstStep ? 'w-full' : 'flex-1'} h-12 rounded-full font-bold bg-gradient-to-r ${step.color} hover:opacity-90`}
            >
              {isLastStep ? 'Get Started!' : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
            >
              Skip tutorial
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TutorialTour;
